import React, { useState, useEffect } from 'react';

const emptyForm = {
    harvestStartDate: '',
    harvestPeakDate: '',
    harvestEndDate: '',
    targetDate: '',
    quantity: '',
    quantityUnit: 'STEMS',
    notes: '',
};

const toEditForm = (harvest) => ({
    harvestStartDate: harvest.harvestStartDate || '',
    harvestPeakDate: harvest.harvestPeakDate || '',
    harvestEndDate: harvest.harvestEndDate || '',
    targetDate: harvest.targetDate || '',
    quantity: harvest.quantity != null ? String(harvest.quantity) : '',
    quantityUnit: harvest.quantityUnit || 'STEMS',
    notes: harvest.notes || '',
});

// Mismas reglas que valida el backend en registerHarvest/updateHarvest: start <= peak <= end.
const validateDates = ({ harvestStartDate, harvestPeakDate, harvestEndDate }) => {
    if (!harvestStartDate) return 'La fecha de Inicio Pre-pico es requerida.';
    if (harvestPeakDate && harvestPeakDate < harvestStartDate) {
        return 'La fecha de Inicio Pico no puede ser anterior a la de Inicio Pre-pico.';
    }
    const afterPeak = harvestPeakDate || harvestStartDate;
    if (harvestEndDate && harvestEndDate < afterPeak) {
        return 'La fecha de Final cosecha no puede ser anterior a la de Inicio Pico/Pre-pico.';
    }
    return null;
};

const RegisterHarvestModal = ({ show, isMultiple, previousHarvestCount, existingHarvest, isLoading, onConfirm, onCancel }) => {
    const [formData, setFormData] = useState(emptyForm);
    const isEditing = !!existingHarvest;

    useEffect(() => {
        if (show) {
            setFormData(isEditing ? toEditForm(existingHarvest) : emptyForm);
        }
    }, [show, isEditing, existingHarvest]);

    if (!show) return null;

    const dateError = validateDates(formData);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (dateError) return;
        onConfirm({
            harvestStartDate: formData.harvestStartDate,
            harvestPeakDate: formData.harvestPeakDate || null,
            harvestEndDate: formData.harvestEndDate || null,
            targetDate: isMultiple ? (formData.targetDate || null) : null,
            quantity: formData.quantity !== '' ? Number(formData.quantity) : null,
            quantityUnit: formData.quantity !== '' ? formData.quantityUnit : null,
            notes: formData.notes || null,
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                    <span className="material-icons text-green-600 text-3xl">agriculture</span>
                    <h5 className="font-bold text-gray-800 mb-0">{isEditing ? 'Completar cosecha' : 'Registrar cosecha'}</h5>
                </div>

                {!isEditing && previousHarvestCount > 0 && (
                    <div className="mb-4 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-xs font-bold text-indigo-600">
                        Ya tienes {previousHarvestCount} cosecha{previousHarvestCount > 1 ? 's' : ''} registrada{previousHarvestCount > 1 ? 's' : ''}.
                    </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block font-semibold mb-1 text-sm">Inicio Pre-pico <span className="text-red-500">*</span></label>
                            <input
                                type="date"
                                name="harvestStartDate"
                                value={formData.harvestStartDate}
                                onChange={handleChange}
                                required
                                className="block w-full p-2 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold mb-1 text-sm">Inicio Pico (Opc.)</label>
                            <input
                                type="date"
                                name="harvestPeakDate"
                                value={formData.harvestPeakDate}
                                onChange={handleChange}
                                min={formData.harvestStartDate || undefined}
                                className="block w-full p-2 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold mb-1 text-sm">Final cosecha (Opc.)</label>
                            <input
                                type="date"
                                name="harvestEndDate"
                                value={formData.harvestEndDate}
                                onChange={handleChange}
                                min={formData.harvestPeakDate || formData.harvestStartDate || undefined}
                                className="block w-full p-2 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        {isMultiple && (
                            <div>
                                <label className="block font-semibold mb-1 text-sm">Fecha objetivo próx. pasada (Opc.)</label>
                                <input
                                    type="date"
                                    name="targetDate"
                                    value={formData.targetDate}
                                    onChange={handleChange}
                                    className="block w-full p-2 border border-gray-300 rounded text-sm"
                                />
                            </div>
                        )}
                    </div>

                    {dateError && (
                        <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs font-bold text-red-500">
                            {dateError}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block font-semibold mb-1 text-sm">Cantidad (Opc.)</label>
                            <input
                                type="number"
                                step="any"
                                min="0"
                                name="quantity"
                                value={formData.quantity}
                                onChange={handleChange}
                                className="block w-full p-2 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold mb-1 text-sm">Unidad</label>
                            <select
                                name="quantityUnit"
                                value={formData.quantityUnit}
                                onChange={handleChange}
                                disabled={formData.quantity === ''}
                                className="block w-full p-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                            >
                                <option value="STEMS">Tallos</option>
                                <option value="KG">Kg</option>
                                <option value="UNITS">Unidades</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block font-semibold mb-1 text-sm">Notas (Opc.)</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows={2}
                            className="block w-full p-2 border border-gray-300 rounded text-sm"
                        />
                    </div>

                    <div className="flex gap-3 justify-end pt-2">
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50"
                            onClick={onCancel}
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 flex items-center gap-2 disabled:opacity-60"
                            disabled={isLoading || !!dateError}
                        >
                            {isLoading
                                ? <>
                                    <span className="material-icons text-sm animate-spin">refresh</span>
                                    Guardando...
                                  </>
                                : <>
                                    <span className="material-icons text-sm">check</span>
                                    {isEditing ? 'Guardar cambios' : 'Registrar cosecha'}
                                  </>
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegisterHarvestModal;
